import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, IconButton, InputAdornment, Link, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { Add, DeleteOutline, EditOutlined, Layers } from "@mui/icons-material";
import { PriceTier, Product } from "../../../types";
import { productService } from "../../../services/productService";
import { formatCurrencyBDT } from "../../../utils/currency";
import { getEffectivePrice, unitPriceFor } from "../../../utils/productPricing";
import { RunSave } from "./useProductQuickSave";

// Combo prices are whole taka — no ".00" on them.
const taka = (n: number) => formatCurrencyBDT(n, { minimumFractionDigits: 0 });
const round2 = (n: number) => Math.round(n * 100) / 100;
const MAX_TIERS = 10;

/** One tier being edited. `each` is what's saved (whole taka); `total` is
 * the pack price shown beside it — typing either one fills in the other. */
interface Draft {
    key: number;
    qty: string;
    each: string;
    total: string;
}

const totalFor = (qty: string, each: string) => {
    const q = Number(qty);
    const e = Number(each);
    return qty !== "" && each !== "" && q > 0 && e > 0 ? String(round2(q * e)) : "";
};

const draftsFrom = (tiers: PriceTier[]): Draft[] =>
    tiers.map((t, i) => {
        const qty = String(Number(t.min_quantity));
        const each = String(Number(t.unit_price));
        return { key: i, qty, each, total: totalFor(qty, each) };
    });

/** Problems that stop a save, worded like the API's (services/price_tiers.py). */
function problemsIn(drafts: Draft[], selling: number): string[] {
    const problems: string[] = [];
    const seen = new Set<number>();
    for (const d of drafts) {
        const q = Number(d.qty);
        const e = Number(d.each);
        if (d.qty === "" || !Number.isInteger(q) || q < 2) problems.push("Each combo starts from 2 pieces or more.");
        else if (seen.has(q)) problems.push(`There are two combo prices for ${q}+ — keep one.`);
        seen.add(q);
        if (d.each === "" || !(e > 0)) problems.push("Enter a price for each combo.");
        else if (!Number.isInteger(e)) problems.push(`Use whole taka for the ${d.qty}+ price.`);
        else if (e >= selling) problems.push(`The ${d.qty}+ price must be below the regular price (${taka(selling)}).`);
    }
    const sorted = drafts
        .map((d) => ({ q: Number(d.qty), e: Number(d.each) }))
        .filter((t) => t.q >= 2 && t.e > 0)
        .sort((a, b) => a.q - b.q);
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].q !== sorted[i - 1].q && sorted[i].e >= sorted[i - 1].e) {
            problems.push(
                `Buying ${sorted[i].q}+ should cost less each than ${sorted[i - 1].q}+ (${taka(sorted[i - 1].e)}).`,
            );
        }
    }
    return Array.from(new Set(problems));
}

interface Props {
    product: Product;
    editable: boolean;
    run: RunSave;
    onDirtyChange: (dirty: boolean) => void;
}

/**
 * Combo prices in the product drawer: "from 4 pieces → ৳250 each". Shown
 * as one line; editing opens rows where staff type the price per piece or
 * the pack total, with a preview of what customers pay.
 */
export const ComboPricesEditor: React.FC<Props> = ({ product, editable, run, onDirtyChange }) => {
    const selling = Number(product.selling_price);
    const cost = Number(product.cost_price);
    const today = getEffectivePrice(product);
    const tiers = useMemo(
        () =>
            (product.price_tiers ?? [])
                .map((t) => ({ min_quantity: Number(t.min_quantity), unit_price: Number(t.unit_price) }))
                .sort((a, b) => a.min_quantity - b.min_quantity),
        [product.price_tiers],
    );
    const has = tiers.length > 0;

    const [open, setOpen] = useState(false);
    const [drafts, setDrafts] = useState<Draft[]>([]);
    const [nextKey, setNextKey] = useState(0);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [showProblems, setShowProblems] = useState(false);

    // Another product, or a fresh copy after saving: close the panel.
    const signature = tiers.map((t) => `${t.min_quantity}:${t.unit_price}`).join(",");
    useEffect(() => {
        setOpen(false);
        setError("");
    }, [product.product_id, signature]);

    const draftSignature = drafts.map((d) => `${Number(d.qty)}:${Number(d.each)}`).join(",");
    const dirty = open && draftSignature !== signature;
    useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

    const start = () => {
        const initial = has ? draftsFrom(tiers) : [{ key: 0, qty: "4", each: "", total: "" }];
        setDrafts(initial);
        setNextKey(initial.length);
        setError("");
        setShowProblems(false);
        setOpen(true);
    };

    const change = (key: number, field: "qty" | "each" | "total", value: string) => {
        setError("");
        setDrafts((prev) =>
            prev.map((d) => {
                if (d.key !== key) return d;
                if (field === "total") {
                    // Prices are whole taka: the nearest one, and the note
                    // under the row offers both when the total doesn't split.
                    const q = Number(d.qty);
                    const each = value !== "" && q > 0 && Number(value) > 0 ? String(Math.round(Number(value) / q)) : d.each;
                    return { ...d, total: value, each };
                }
                const next = { ...d, [field]: value };
                return { ...next, total: totalFor(next.qty, next.each) };
            }),
        );
    };

    const addTier = () => {
        const last = drafts[drafts.length - 1];
        const qty = last && Number(last.qty) >= 2 ? String(Number(last.qty) + 2) : "4";
        setDrafts((prev) => [...prev, { key: nextKey, qty, each: "", total: "" }]);
        setNextKey((k) => k + 1);
    };

    const removeTier = (key: number) => setDrafts((prev) => prev.filter((d) => d.key !== key));

    const problems = problemsIn(drafts, selling);

    const submit = async (list: Draft[]) => {
        if (!list.length && !has) return setOpen(false);
        if (problemsIn(list, selling).length) return setShowProblems(true);
        setBusy(true);
        const payload: PriceTier[] = list.map((d) => ({ min_quantity: Number(d.qty), unit_price: round2(Number(d.each)) }));
        const failed = await run(
            () => productService.updatePriceTiers(product.product_id, payload),
            !payload.length ? "Combo prices removed" : has ? "Combo prices updated" : "Combo prices added",
        );
        setBusy(false);
        if (failed) setError(failed);
    };

    // What customers would pay with the drafts, at a few telling quantities.
    const valid = drafts.filter((d) => Number(d.qty) >= 2 && Number(d.each) > 0);
    const preview = useMemo(() => {
        const draftProduct = {
            ...product,
            price_tiers: valid.map((d) => ({ min_quantity: Number(d.qty), unit_price: Number(d.each) })),
        };
        const qtys = new Set<number>([1]);
        valid.forEach((d) => {
            qtys.add(Number(d.qty));
        });
        const sorted = Array.from(qtys).sort((a, b) => a - b);
        // One step past the first combo shows that it keeps applying.
        if (sorted.length > 1) qtys.add(sorted[1] + 1);
        return Array.from(qtys)
            .sort((a, b) => a - b)
            .slice(0, 6)
            .map((q) => ({ q, total: round2(unitPriceFor(draftProduct, q) * q) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftSignature, today]);

    const summary = tiers.map((t) => `${t.min_quantity}+ ${taka(t.unit_price)}`).join(" · ");

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minHeight: 36 }}>
                <Layers fontSize="small" color={has ? "secondary" : "disabled"} />
                <Typography variant="body2" color="text.secondary">
                    Combo prices
                </Typography>
                <Typography variant="body2" fontWeight={has ? 600 : 400} color={has ? "text.primary" : "text.secondary"}>
                    {has ? summary : "None"}
                </Typography>
                {editable && !open && (
                    <Button
                        size="small"
                        startIcon={has ? <EditOutlined fontSize="small" /> : undefined}
                        onClick={start}
                        sx={{ ml: "auto" }}
                    >
                        {has ? "Edit" : "Add combo"}
                    </Button>
                )}
            </Box>

            {open && (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 1.5, bgcolor: "action.hover" }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        Buying more of this product lowers the price of every piece — on the website, in orders and at POS.
                        Type the price per piece or the pack total, in whole taka.
                    </Typography>

                    <Box sx={{ display: "grid", gap: 1.5 }}>
                        {drafts.map((d) => {
                            const each = Number(d.each);
                            const q = Number(d.qty);
                            const typedTotal = Number(d.total);
                            // A pack total that doesn't split into whole taka per piece.
                            const uneven = q >= 2 && d.total !== "" && typedTotal > 0 && typedTotal % q !== 0;
                            const belowCost = each > 0 && each < cost;
                            return (
                                <Box key={d.key}>
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="From (pcs)"
                                            value={d.qty}
                                            onChange={(e) => change(d.key, "qty", e.target.value)}
                                            inputProps={{ min: 2, step: 1, inputMode: "numeric" }}
                                            sx={{ width: 104, flexShrink: 0, bgcolor: "background.paper" }}
                                        />
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Price each"
                                            value={d.each}
                                            onChange={(e) => change(d.key, "each", e.target.value)}
                                            inputProps={{ min: 1, step: 1, inputMode: "numeric" }}
                                            InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
                                            sx={{ flex: "1 1 120px", minWidth: 120, bgcolor: "background.paper" }}
                                        />
                                        <TextField
                                            size="small"
                                            type="number"
                                            label={q >= 2 ? `${q} for` : "Pack total"}
                                            value={d.total}
                                            onChange={(e) => change(d.key, "total", e.target.value)}
                                            inputProps={{ min: 1, step: 1, inputMode: "numeric" }}
                                            InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
                                            sx={{ flex: "1 1 120px", minWidth: 120, bgcolor: "background.paper" }}
                                        />
                                        <Tooltip title="Remove this combo">
                                            <IconButton
                                                aria-label={`Remove the ${d.qty || "new"}+ combo`}
                                                onClick={() => removeTier(d.key)}
                                                sx={{ flexShrink: 0 }}
                                            >
                                                <DeleteOutline fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    {each > 0 && each < selling && (
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="success.main" fontWeight={600}>
                                            Saves {taka(round2((selling - each) * (q || 1)))}
                                            {q >= 2 && ` on ${q}`} · −{Math.round((1 - each / selling) * 100)}%
                                            {belowCost && (
                                                <Box component="span" color="error.main">
                                                    {" "}· below cost {taka(cost)}
                                                </Box>
                                            )}
                                        </Typography>
                                    )}
                                    {uneven && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.5 }}>
                                            <Typography variant="caption" color="warning.main" fontWeight={600}>
                                                {taka(typedTotal)} doesn't split evenly over {q} — pick one:
                                            </Typography>
                                            {[Math.floor(typedTotal / q), Math.ceil(typedTotal / q)].map((whole) => (
                                                <Chip
                                                    key={whole}
                                                    size="small"
                                                    variant="outlined"
                                                    label={`${taka(whole)} each → ${taka(whole * q)}`}
                                                    onClick={() => change(d.key, "each", String(whole))}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {drafts.length < MAX_TIERS && (
                        <Button size="small" startIcon={<Add />} onClick={addTier} sx={{ mt: 1 }}>
                            {drafts.length ? "Add another" : "Add a combo"}
                        </Button>
                    )}

                    {valid.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Customers pay
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                                {preview.map(({ q, total }) => (
                                    <Chip
                                        key={q}
                                        size="small"
                                        sx={{ bgcolor: "background.paper", fontVariantNumeric: "tabular-nums" }}
                                        label={`${q} → ${taka(total)}`}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {today < selling && valid.some((d) => Number(d.each) >= today) && (
                        <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
                            While today's {taka(today)} sale price runs, it beats combos priced at or above it — customers
                            always get the lower price.
                        </Alert>
                    )}

                    {(showProblems ? problems : []).concat(error ? [error] : []).map((p) => (
                        <Typography key={p} variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                            {p}
                        </Typography>
                    ))}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                        {has && (
                            <Link
                                component="button"
                                type="button"
                                color="error"
                                variant="body2"
                                onClick={() => void submit([])}
                                disabled={busy}
                            >
                                Remove combo prices
                            </Link>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" color="inherit" onClick={() => setOpen(false)} disabled={busy}>
                            Cancel
                        </Button>
                        <Button size="small" variant="contained" onClick={() => void submit(drafts)} disabled={busy}>
                            {busy ? "Saving…" : "Save combo"}
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default ComboPricesEditor;
